/** v1 disposal types. Stage and Fan both use hash(submissionId) % count. */
export const RAFFLE_DISPOSAL_TYPES = ['fly-off', 'flames', 'gravity'] as const;

export type RaffleDisposalType = (typeof RAFFLE_DISPOSAL_TYPES)[number];

/** FNV-1a 32-bit so Stage and Fan compute the same integer in every JS runtime. */
export function hashSubmissionId(submissionId: string): number {
  let hash = 2166136261;
  for (let i = 0; i < submissionId.length; i += 1) {
    hash ^= submissionId.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function disposalTypeForSubmissionId(
  submissionId: string,
): RaffleDisposalType {
  return RAFFLE_DISPOSAL_TYPES[
    hashSubmissionId(submissionId) % RAFFLE_DISPOSAL_TYPES.length
  ];
}

export function raffleFlyOffDelta(submissionId: string): {
  x: number;
  y: number;
  rotate: number;
} {
  const hash = hashSubmissionId(submissionId);
  const quadrant = hash % 4;
  const baseAngle = (quadrant * 90 + 25 + (hash % 20)) * (Math.PI / 180);
  const distance = 160 + (hash % 80);
  const rotateSign = quadrant % 2 === 0 ? 1 : -1;
  return {
    x: Math.cos(baseAngle) * distance,
    y: Math.sin(baseAngle) * distance - 24,
    rotate: rotateSign * (18 + (hash % 8)),
  };
}

export function raffleDisposalExit(submissionId: string) {
  const type = disposalTypeForSubmissionId(submissionId);
  const hash = hashSubmissionId(submissionId);

  if (type === 'fly-off') {
    const delta = raffleFlyOffDelta(submissionId);
    return {
      x: delta.x,
      y: delta.y,
      rotate: delta.rotate,
      opacity: 0,
      scale: 0.92,
      filter: 'brightness(1.15)',
      transition: { duration: 0.7, ease: [0.15, 0.7, 0.25, 1] as const },
    };
  }

  if (type === 'gravity') {
    const drift = ((hash >>> 10) % 48) - 24;
    return {
      y: [0, 28, 140, 420],
      x: [0, drift * 0.4, -drift * 0.35, drift],
      rotate: [0, -10, 14, 26],
      opacity: [1, 1, 0.9, 0],
      scale: [1, 1.02, 0.98, 0.94],
      filter: 'brightness(0.85)',
      transition: {
        duration: 0.9,
        times: [0, 0.18, 0.5, 1],
        ease: 'easeIn' as const,
      },
    };
  }

  return {
    scale: [1, 1.04, 0.55, 0.12],
    y: [0, -6, 8, 18],
    opacity: [1, 1, 0.7, 0],
    filter: [
      'brightness(1) saturate(1)',
      'brightness(0.55) saturate(1.8) sepia(0.35)',
      'brightness(0.25) saturate(2.4)',
      'brightness(2.8) saturate(2)',
    ],
    transition: {
      duration: 0.8,
      times: [0, 0.22, 0.62, 1],
      ease: 'easeIn' as const,
    },
  };
}
